import { useCallback, useMemo } from "react";
import { Flex, Spinner, Button, DropdownMenu } from "@radix-ui/themes";
import { QuackChatConfigResponse } from "@/components/quack-chat/QuackChatConfig.model";
import { QuackChat } from "@/components/quack-chat/QuackChat";
import { useQuackchatPlatformConfiguration } from "@/api/quackchat/configuration/useQuackchatPlatformConfiguration";
import { InlineTextField } from "@/components/shared/inputs/inilie-text-field/InlineTextField";
import { FieldGroup, OptionalFieldGroup } from "../parts/FieldGroup";
import { QuackchatSystemMessageConfiguration } from "../quackchat-channel/system-message/QuackchatSystemMessageConfiguration";
import { Message } from "@/components/quack-chat/core/entities/Message";
import { AutomationConfigurationContentHeader } from "../parts/AutomationConfigurationContentHeader";
import { UpdateSimulatorGoodFeedbackSeperation } from "../parts/UpdateSimulatorGoodFeedbackSeperation";
import { UpdateCustomTonePrompt } from "../parts/UpdateCustomTonePrompt";
import { AskEmailInFirstClientMessage } from "../parts/AskEmailInFirstClientMessage";
import Checkbox from "@/components/shared/inputs/checkbox";
import { AllowUploadFileBeforeEscalation } from "../parts/AllowUploadFileBeforeEscalation";
import { useGetQuackChatSystemMessage } from "./system-message/useGetQuackChatSystemMessage";
import { useSimulator } from "@/v2/features/simulator/context/SimulatorWrapper";
import { toast } from "sonner";
import { Download, Upload } from "lucide-react";
import { DotsVerticalIcon } from "@radix-ui/react-icons";
import { useQuackchatSessionConfiguration } from "@/api/quackchat/configuration/useQuackchatSessionConfiguration";
import { AllowCloseAndOpenChat } from "../parts/AllowCloseAndOpenChat";
import { SendQuestionsByContextAtBeginning } from "../parts/SendQuestionsByContextAtBeginning";
import { SendWalkthroughAtBeginning } from "../parts/SendWalkthroughAtBeginning";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { QuackchatLoginEnforcement } from "./QuackchatLoginEnforcement";
import { useBrands } from "@/api/useBrands";
import { TenantBrand } from "@/v2/entities/brand/Brand.model";
import Select from "@/components/shared/select";
import { EnableNewMessageSound } from "../parts/EnableNewMessageSound";
import { ArticleLinksField } from "../parts/ArticleLinksField";

export function QuackchatChannelGeneralConfiguration({
  simulatorId,
}: {
  simulatorId?: string;
}) {
  const { updateRelease, currentRelease } = useSimulator();

  const {
    data: configuration,
    isLoading,
    updateStatus,
    update: updateConfiguration,
  } = useQuackchatPlatformConfiguration(simulatorId);

  const { data: welcomeMessage } = useGetQuackChatSystemMessage(
    "WELCOME",
    simulatorId,
  );

  const questionsFlag = useFlag("questionscontextquackchat");
  const walkthroughFlag = useFlag("walkthroughgeneral");
  const loginEnforcementFlag = useFlag("login-enforcement");
  const knowledgeBrandIdFlag = useFlag("knowledgeBrandId");

  const { data: brands, isLoading: loadingBrands } = useBrands();

  const {
    update: updateQuackchatSessionConfiguration,
    data: quackchatSessionConfiguration,
  } = useQuackchatSessionConfiguration(simulatorId);

  const messages = useMemo<Message[] | undefined>(() => {
    if (welcomeMessage == null || welcomeMessage.enabled === false) {
      return;
    }

    return [
      {
        action: "standard_message",
        author: "AUTO_AGENT",
        text: welcomeMessage.message,
        deduplicationKey: "example_message",
        createdAt: "2021-09-01T12:00:00Z",
        data: {},
      },
    ];
  }, [welcomeMessage]);

  const updateChatSettings = useCallback(
    (chatSettings: Partial<QuackChatConfigResponse["chatSettings"]>) => {
      if (configuration == null) {
        return;
      }

      updateConfiguration({
        ...configuration,
        chatSettings: {
          ...configuration.chatSettings,
          ...chatSettings,
        },
      });
    },
    [configuration, updateConfiguration],
  );

  const handleUpdateRelease = useCallback(
    (payload: { knowledgeBrandId?: number }) => {
      if (currentRelease?.id) {
        updateRelease(String(currentRelease.id), {
          simulatorId: currentRelease.simulatorId,
          ...payload,
        });
      } else {
        toast.error("Something went wrong, please try again");
      }
    },
    [currentRelease?.id, updateRelease],
  );

  const updateAllowUploadFileBeforeEscalation = useCallback(
    (enabled: boolean) => {
      updateChatSettings({ allowUploadFileBeforeEscalation: enabled });
    },
    [updateChatSettings],
  );

  const updateEnableNewMessageSound = useCallback(
    (enabled: boolean) => {
      updateChatSettings({ enableNewMessageSound: enabled });
    },
    [updateChatSettings],
  );

  const updateShowCloseChatConfirmation = useCallback(
    (enabled: boolean) => {
      updateChatSettings({ showCloseChatConfirmation: enabled });
    },
    [updateChatSettings],
  );

  const updateContextAwareQuestionsConfiguration = useCallback(
    (isToggleOn: boolean, emails: string[]) => {
      if (configuration == null) {
        return;
      }

      updateConfiguration({
        ...configuration,
        contextAwareQuestionsConfiguration: {
          isToggleOn,
          emails: isToggleOn ? emails : [],
        },
      });

      updateQuackchatSessionConfiguration({
        useContextAwareQuestions: isToggleOn,
      });
    },
    [configuration, updateConfiguration, updateQuackchatSessionConfiguration],
  );

  const updateContextArrowsHelperConfiguration = useCallback(
    (isToggleOn: boolean, emails: string[]) => {
      if (configuration == null) {
        return;
      }

      updateConfiguration({
        ...configuration,
        contextArrowsHelperConfiguration: {
          isToggleOn,
          emails: isToggleOn ? emails : [],
        },
      });

      updateQuackchatSessionConfiguration({
        useContextArrowsHelper: isToggleOn,
      });
    },
    [configuration, updateConfiguration, updateQuackchatSessionConfiguration],
  );

  const exportConfiguration = useCallback(() => {
    if (!configuration) return;

    const exportData = {
      chatSettings: configuration.chatSettings,
      exportedAt: new Date().toISOString(),
      version: "1.0",
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `quackchat-configuration-${
      new Date().toISOString().split("T")[0]
    }.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [configuration]);

  const importConfiguration = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";

    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const importData = JSON.parse(event.target?.result as string);

          if (!importData.chatSettings) {
            throw new Error("Invalid configuration file");
          }

          updateConfiguration({
            ...configuration!,
            chatSettings: {
              ...configuration!.chatSettings,
              ...importData.chatSettings,
            },
          });
        } catch (error) {
          console.error("Error importing configuration:", error);
          alert("Error importing configuration. Please check the file format.");
        }
      };
      reader.readAsText(file);
    };

    input.click();
  }, [configuration, updateConfiguration]);

  if (isLoading || configuration == null) {
    return <Spinner />;
  }

  const { chatSettings } = configuration;

  return (
    <Flex maxWidth={"100%"} gap={"24px"} overflow={"hidden"}>
      <Flex
        flexGrow={"1"}
        maxWidth={"100%"}
        direction={"column"}
        gap={"24px"}
        overflow={"hidden"}
      >
        <Flex align="center" justify="between">
          <AutomationConfigurationContentHeader
            title={"General"}
            status={updateStatus}
          />

          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <Button variant="ghost" size="1">
                <DotsVerticalIcon />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content size="2">
              <DropdownMenu.Item
                onClick={exportConfiguration}
                style={{ color: "inherit" }}
              >
                <Download size={16} />
                Export
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onClick={importConfiguration}
                style={{ color: "inherit" }}
              >
                <Upload size={16} />
                Import
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </Flex>

        <Flex direction={"column"}>
          <FieldGroup horizontal description={"Chat Header"}>
            <InlineTextField
              value={chatSettings.name}
              onChange={(newValue) =>
                updateChatSettings({
                  name: newValue,
                })
              }
              placeholder={"Team"}
            />
          </FieldGroup>

          <FieldGroup horizontal description={"Quack Agent Name"}>
            <InlineTextField
              value={chatSettings?.quackAgentName}
              onChange={(newValue) =>
                updateChatSettings({
                  quackAgentName: newValue,
                })
              }
              placeholder={"AI Assistant"}
            />
          </FieldGroup>

          <FieldGroup horizontal description={"Place Holder Input"}>
            <InlineTextField
              value={chatSettings.placeholder}
              onChange={(newValue) =>
                updateChatSettings({
                  placeholder: newValue === "" ? undefined : newValue,
                })
              }
              placeholder={"Ask anything..."}
            />
          </FieldGroup>

          <FieldGroup
            label={"Brand Association"}
            description={"Choose the brand to associate with the chat channel."}
          >
            <Select
              options={
                brands?.map((x: TenantBrand) => ({
                  label: x.name,
                  value: String(x.id),
                })) ?? []
              }
              value={
                currentRelease?.knowledgeBrandId
                  ? String(currentRelease.knowledgeBrandId)
                  : undefined
              }
              onChange={(newValue) =>
                handleUpdateRelease({ knowledgeBrandId: Number(newValue) })
              }
              loading={loadingBrands}
            />
          </FieldGroup>

          <FieldGroup
            label={"Help Center Link"}
            description={"Provide a URL to your help center for customers to access additional support resources."}
          >
            <InlineTextField
              value={chatSettings.helpCenterLink}
              onChange={(newValue) =>
                updateChatSettings({ helpCenterLink: newValue === "" ? undefined : newValue })
              }
              placeholder={"https://help.example.com"}
            />
          </FieldGroup>

          <EnableNewMessageSound
            enabled={chatSettings.enableNewMessageSound}
            onChange={updateEnableNewMessageSound}
          />

          <OptionalFieldGroup
            label={"Show Close Chat Confirmation"}
            description={"Show a confirmation prompt before users close the chat."}
            enabled={chatSettings.showCloseChatConfirmation ?? false}
            toggleEnabled={updateShowCloseChatConfirmation}
          />

          <FieldGroup
            label={"Customize Messages"}
            description={
              "Customize messages to be sent automatically at different points in the conversation flow."
            }
          >
            <QuackchatSystemMessageConfiguration
              config={configuration}
              defaultMessageType={"WELCOME"}
              simulatorId={simulatorId}
            />
          </FieldGroup>

          <FieldGroup
            label={"Strict Mode"}
            description={
              "When enabled Quack won't answer on question that wasn't trained."
            }
          >
            <Checkbox
              label="Enable Strict Mode"
              value={currentRelease?.strictMode ?? false}
              onChange={(checked) => {
                if (currentRelease?.id) {
                  updateRelease(String(currentRelease.id), {
                    simulatorId: currentRelease.simulatorId,
                    strictMode: checked,
                  });
                } else {
                  toast.error("Something went wrong, please try again");
                }
              }}
            />
          </FieldGroup>

          <ArticleLinksField />

          <FieldGroup
            label={"Prioritize Brief Links"}
            description={
              "When a topic brief provides a link, hide knowledge-base/source links so Quack sends only the link from the brief."
            }
          >
            <Checkbox
              label="Hide source links when the brief has a link"
              value={currentRelease?.hideSourceUrls ?? false}
              onChange={(checked) => {
                if (currentRelease?.id) {
                  updateRelease(String(currentRelease.id), {
                    simulatorId: currentRelease.simulatorId,
                    hideSourceUrls: checked,
                  });
                } else {
                  toast.error("Something went wrong, please try again");
                }
              }}
            />
          </FieldGroup>

          <UpdateSimulatorGoodFeedbackSeperation />
          <UpdateCustomTonePrompt />

          <AskEmailInFirstClientMessage
            quackchatSessionConfiguration={quackchatSessionConfiguration}
            updateQuackchatSessionConfiguration={
              updateQuackchatSessionConfiguration
            }
          />

          <AllowUploadFileBeforeEscalation
            enabled={chatSettings.allowUploadFileBeforeEscalation}
            onChange={updateAllowUploadFileBeforeEscalation}
          />

          <AllowCloseAndOpenChat
            chatSettings={chatSettings}
            updateChatSettings={updateChatSettings}
          />

          {questionsFlag && (
            <SendQuestionsByContextAtBeginning
              enabled={
                configuration?.contextAwareQuestionsConfiguration?.isToggleOn ??
                false
              }
              emails={
                configuration?.contextAwareQuestionsConfiguration?.emails ?? []
              }
              onChange={(enabled) =>
                updateContextAwareQuestionsConfiguration(
                  enabled,
                  configuration?.contextAwareQuestionsConfiguration?.emails ??
                    [],
                )
              }
              onEmailsChange={(emails) =>
                updateContextAwareQuestionsConfiguration(
                  configuration?.contextAwareQuestionsConfiguration
                    ?.isToggleOn ?? false,
                  emails,
                )
              }
            />
          )}

          {walkthroughFlag && (
            <SendWalkthroughAtBeginning
              enabled={
                configuration?.contextArrowsHelperConfiguration?.isToggleOn ??
                false
              }
              emails={
                configuration?.contextArrowsHelperConfiguration?.emails ?? []
              }
              onChange={(enabled) =>
                updateContextArrowsHelperConfiguration(
                  enabled,
                  configuration?.contextArrowsHelperConfiguration?.emails ?? [],
                )
              }
              onEmailsChange={(emails) =>
                updateContextArrowsHelperConfiguration(
                  configuration?.contextArrowsHelperConfiguration?.isToggleOn ??
                    false,
                  emails,
                )
              }
            />
          )}

          {loginEnforcementFlag && <QuackchatLoginEnforcement />}
        </Flex>
      </Flex>

      <Flex flexShrink={"0"}>
        <QuackChat config={configuration} messages={messages} />
      </Flex>
    </Flex>
  );
}
